import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Loader2, RefreshCw, History, AlertCircle, Play } from 'lucide-react';
import { formatDateTime } from '../../lib/utils';

const STATUS_STYLES = {
  succeeded: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  skipped: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  received: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
};

export default function StripeWebhookEventLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [replayingId, setReplayingId] = useState(null);

  const fetchEvents = useCallback(async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const { data } = await adminAPI.getStripeWebhookEvents(25);
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const handleReplay = async (eventId) => {
    setReplayingId(eventId);
    try {
      const { data } = await adminAPI.replayStripeWebhookEvent(eventId);
      if (data.status === 'succeeded') {
        toast.success(`Replay succeeded — ${data.detail || ''}`);
      } else if (data.status === 'skipped') {
        toast.info(`Replay skipped — ${data.detail || 'no action needed'}`);
      } else {
        toast.error(`Replay failed — ${data.detail || 'unknown error'}`);
      }
      await fetchEvents(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to replay event');
    } finally {
      setReplayingId(null);
    }
  };

  useEffect(() => {
    fetchEvents(false);
  }, [fetchEvents]);

  if (loading) {
    return (
      <Card className="mt-6" data-testid="webhook-events-card">
        <CardContent className="p-6 flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading webhook events…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6" data-testid="webhook-events-card">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#635bff]" />
            Webhook Event Log
          </CardTitle>
          <CardDescription>
            Last {events.length || 0} events Stripe delivered to your webhook endpoint.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchEvents(true)}
          disabled={refreshing}
          data-testid="refresh-webhook-events-btn"
        >
          {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground flex flex-col items-center gap-2" data-testid="webhook-events-empty">
            <AlertCircle className="w-5 h-5" />
            No webhook events received yet. Send a test event from your Stripe dashboard to see it appear here within a few seconds.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Received</TableHead>
                  <TableHead>Event type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="hidden md:table-cell">Session</TableHead>
                  <TableHead className="w-[80px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((e) => {
                  const replayable = !!e.session_id;
                  return (
                  <TableRow key={e.event_id} data-testid={`webhook-event-row-${e.event_id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {e.received_at ? formatDateTime(e.received_at) : '—'}
                      {e.replay_of && (
                        <Badge variant="outline" className="ml-1 text-[10px] py-0">replay</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{e.event_type || '—'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_STYLES[e.processing_status] || STATUS_STYLES.received} variant="secondary">
                        {e.processing_status}
                      </Badge>
                      {e.error_message && (
                        <p className="text-[11px] text-red-600 mt-1 max-w-[260px] truncate" title={e.error_message}>
                          {e.error_message}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {e.amount != null ? `$${Number(e.amount).toFixed(2)} ${(e.currency || 'usd').toUpperCase()}` : '—'}
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-[11px] text-muted-foreground truncate max-w-[180px]" title={e.session_id || ''}>
                      {e.session_id || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={!replayable || replayingId === e.event_id}
                                onClick={() => handleReplay(e.event_id)}
                                data-testid={`replay-event-${e.event_id}`}
                                className="h-7 px-2"
                              >
                                {replayingId === e.event_id ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Play className="w-3.5 h-3.5" />
                                )}
                                <span className="ml-1 text-xs">Replay</span>
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            {replayable
                              ? 'Re-runs the idempotent post-processing (mark txn paid + extend sub).'
                              : 'Cannot replay — event failed at signature/parse. Resend from Stripe dashboard.'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
